// jsx/components/Scoring/ScoringView.jsx
import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Form,
  Card,
  Row,
  Col,
  Alert,
  Spinner,
  ButtonGroup,
  OverlayTrigger,
  Tooltip,
} from 'react-bootstrap';

import PageTitle from '../../layouts/PageTitle';
import GuidelinesModal from './GuidelinesModal.jsx';
import { NOMINATION_STATUS, STATUS_STYLES } from '../../../utils/constants';

const ElectionTypeToggle = ({
  availableTypes,
  activeType,
  onTypeChange,
  canScoreMultipleTypes,
}) => {
  if (!canScoreMultipleTypes) return null;

  return (
    <div className="d-flex align-items-center justify-content-center gap-2 mb-xl-5">
      <ButtonGroup>
        {availableTypes.map((type) => (
          <Button
            key={type.id}
            variant={activeType === type.id ? 'primary' : 'outline-primary'}
            size="sm"
            onClick={() => onTypeChange(type.id)}
            className={`min-width-250 ${activeType === type.id ? 'fw-bold' : ''}`}
          >
            {type.name}
          </Button>
        ))}
      </ButtonGroup>
    </div>
  );
};

const GuidelinesButton = ({ criterionKey, onShowGuidelines }) => (
  <Button
    variant="warning"
    size="sm"
    className="px-3 py-1"
    onClick={() => onShowGuidelines(criterionKey)}
  >
    Guidelines
  </Button>
);

const ScoreInput = ({
  value,
  onChange,
  min,
  max,
  adjustedMax,
  disabled = false,
  isValid = true,
}) => {
  const effectiveMax = adjustedMax || max;

  const handleKeyDown = (e) => {
    if (
      [8, 9, 27, 13, 35, 36, 37, 39, 46].indexOf(e.keyCode) !== -1 ||
      (e.keyCode === 65 && e.ctrlKey === true) ||
      (e.keyCode === 67 && e.ctrlKey === true) ||
      (e.keyCode === 86 && e.ctrlKey === true) ||
      (e.keyCode === 88 && e.ctrlKey === true)
    ) {
      return;
    }

    if (
      (e.shiftKey || e.keyCode < 48 || e.keyCode > 57) &&
      (e.keyCode < 96 || e.keyCode > 105)
    ) {
      e.preventDefault();
    }
  };

  const handleChange = (e) => {
    const inputValue = e.target.value;
    const numericValue = inputValue.replace(/[^0-9]/g, '');
    const intValue = numericValue === '' ? 0 : parseInt(numericValue, 10);
    onChange(intValue);
  };

  return (
    <Form.Control
      type="text"
      inputMode="numeric"
      pattern="[0-9]*"
      value={value || ''}
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      min={min}
      max={effectiveMax}
      disabled={disabled}
      className={`text-center scoring-view__score-input ${!isValid ? 'is-invalid' : ''} ${disabled ? 'bg-light' : ''}`}
      placeholder="0"
    />
  );
};

const CandidateRow = ({
  candidate,
  scores = {},
  matrix,
  onScoreChange,
  onCommentsChange,
  hasConflict = false,
  validationErrors = [],
  isLoading = false,
}) => {
  const statusInfo = STATUS_STYLES[candidate.nominationStatusTypeId];
  const isShortlisted =
    candidate.nominationStatusTypeId === NOMINATION_STATUS.SHORTLISTED;

  const isAdjustedScore = matrix.criteria.some((criterion) => {
    const score = scores[criterion.key] || 0;
    return criterion.adjustedMax !== undefined && score > criterion.max;
  });

  const commentsRequired =
    isAdjustedScore && (!scores.comments || scores.comments.trim() === '');

  const totalScore = matrix.criteria.reduce(
    (total, criterion) => total + (scores[criterion.key] || 0),
    0,
  );

  const isValidTotal =
    totalScore <= 100 && totalScore > 0 && validationErrors.length === 0;
  const hasScores = matrix.criteria.some(
    (criterion) => scores[criterion.key] > 0,
  );

  const getTotalScoreClassName = () => {
    const baseClasses = 'text-center fw-bold scoring-view__score-input';

    if (hasConflict) return `${baseClasses} bg-light text-muted`;
    if (totalScore === 0) return `${baseClasses} border-warning text-warning`;
    if (isValidTotal) return `${baseClasses} border-success text-success`;
    if (hasScores) return `${baseClasses} border-danger text-danger`;
    return `${baseClasses} text-secondary`;
  };

  const getValidationIcon = () => {
    if (hasConflict) return '';
    if (isValidTotal) return '✓';
    if (validationErrors.length > 0) return '✗';
    return '';
  };

  const getValidationIconClass = () => {
    const icon = getValidationIcon();
    if (icon === '✓') return 'text-success';
    if (icon === '✗') return 'text-danger';
    return 'text-muted';
  };

  const shouldShowErrorMessage = () => {
    if (hasConflict) return false;
    if (totalScore === 0) return true;
    return totalScore > 100 && !isValidTotal && hasScores;
  };

  const getPlaceholderText = () => {
    if (hasConflict) return 'Scoring disabled due to conflict';
    if (isAdjustedScore) return 'Required for adjusted scores';
    return 'Optional comments...';
  };

  const getTextareaClassName = () => {
    const baseClass = 'small scoring-view__textarea-no-resize';
    if (hasConflict) return `${baseClass} bg-light text-muted`;
    if (commentsRequired) return `${baseClass} border-danger`;
    return baseClass;
  };

  const getCharCountClassName = () =>
    commentsRequired ? 'text-danger' : 'text-muted';

  return (
    <tr className={hasConflict ? 'table-secondary opacity-75' : ''}>
      <td className="align-middle py-3">
        <div
          className={`d-flex flex-column ${hasConflict ? 'text-muted' : ''}`}
        >
          <p>
            {candidate.name}{' '}
            {statusInfo && !hasConflict && isShortlisted && (
              <span className={`${statusInfo.variant}-span`}>
                {statusInfo.label}
              </span>
            )}
          </p>

          {hasConflict && (
            <small className="text-muted mt-1 d-flex align-items-center">
              <i className="fas fa-exclamation-triangle me-1 text-warning" />
              Conflict of Interest
            </small>
          )}
        </div>
      </td>

      {matrix.criteria.map((criterion) => {
        const errorMessage =
          validationErrors.find((error) => error.includes(criterion.label)) ||
          '';
        const isFieldValid = !validationErrors.some((error) =>
          error.includes(criterion.label),
        );

        return (
          <td key={criterion.key} className="text-center align-middle py-3">
            <div className="d-flex flex-column align-items-center gap-1">
              <ScoreInput
                value={scores[criterion.key]}
                onChange={(value) =>
                  onScoreChange(candidate.candidateId, criterion.key, value)
                }
                min={criterion.min}
                max={criterion.max}
                adjustedMax={criterion.adjustedMax}
                disabled={hasConflict || isLoading}
                isValid={isFieldValid}
                errorMessage={errorMessage}
                criterionLabel={criterion.label}
              />
              {!hasConflict && !isLoading && (
                <OverlayTrigger
                  placement="bottom"
                  overlay={
                    <Tooltip id={`tooltip-${criterion.key}`}>
                      <div className="fs-6">
                        <div>
                          <strong>Base range:</strong> {criterion.min}-
                          {criterion.max}
                        </div>
                        {criterion.adjustedMax && (
                          <>
                            <div>
                              <strong>Adjusted range:</strong> {criterion.min}-
                              {criterion.adjustedMax}
                            </div>
                            <div className="text-warning mt-1">
                              <small>
                                Comments required for adjusted scores (
                                {criterion.max + 1}+)
                              </small>
                            </div>
                          </>
                        )}
                      </div>
                    </Tooltip>
                  }
                >
                  <i
                    className={`fas fa-info-circle ${!isFieldValid ? 'text-danger' : 'text-muted'}`}
                    style={{ fontSize: '0.8rem', cursor: 'help' }}
                  />
                </OverlayTrigger>
              )}

              {!hasConflict && isLoading && (
                <Spinner animation="border" size="sm" />
              )}
            </div>
          </td>
        );
      })}

      <td className="text-center align-middle py-3">
        <div className="d-flex flex-column align-items-center gap-1">
          <Form.Control
            type="number"
            value={totalScore}
            disabled
            className={getTotalScoreClassName()}
            readOnly
          />
          <div className="d-flex flex-column align-items-center">
            {!hasConflict && !shouldShowErrorMessage() && (
              <small className={getValidationIconClass()}>
                {getValidationIcon()}
              </small>
            )}
            {shouldShowErrorMessage() && (
              <small
                className={`mt-1 fw-medium ${totalScore === 0 ? 'text-warning' : 'text-danger'}`}
              >
                {totalScore === 0 ? 'Score required' : 'Must be 100 or less'}
              </small>
            )}
          </div>
        </div>
      </td>

      <td className="align-middle py-3">
        <Form.Control
          as="textarea"
          rows={2}
          value={scores.comments || ''}
          onChange={(e) =>
            onCommentsChange(candidate.candidateId, e.target.value)
          }
          placeholder={getPlaceholderText()}
          maxLength={300}
          disabled={hasConflict || isLoading}
          className={getTextareaClassName()}
        />
        <div className="d-flex justify-content-between mt-1">
          <small className={getCharCountClassName()}>
            {commentsRequired && (
              <i className="fas fa-exclamation-triangle me-1" />
            )}
            {(scores.comments || '').length}/300 characters
            {isAdjustedScore && ' (Required)'}
          </small>
        </div>
      </td>
    </tr>
  );
};

const ScoringView = ({
  onBackToShortlist,
  notificationMessage,
  notificationTitle,
  useScoringResults,
}) => {
  const [showGuidelines, setShowGuidelines] = useState(false);
  const [currentGuideline, setCurrentGuideline] = useState(null);

  const {
    candidates,
    scoringData,
    isLoading,
    activeElectionTypeId,
    canScoreMultipleTypes,
    handleScoreChange,
    handleCommentsChange,
    handleSaveScoring,
    setActiveElectionTypeId,
    getScoringMatrix,
    validateScores,
    getCommitteeInfo,
    getAvailableElectionTypes,
    isValidCandidate,
  } = useScoringResults;

  const sortedCandidates = useMemo(
    () => [...candidates].sort((a, b) => a.name.localeCompare(b.name)),
    [candidates],
  );

  const matrix = getScoringMatrix();
  const committeeInfo = getCommitteeInfo();
  const validationErrors = validateScores();
  const availableElectionTypes = getAvailableElectionTypes();

  const handleShowGuidelines = (criterionKey) => {
    setCurrentGuideline(criterionKey);
    setShowGuidelines(true);
  };

  const handleSaveAndReturn = async () => {
    await handleSaveScoring();
  };

  const getScoredCount = () =>
    sortedCandidates.filter((candidate) => {
      if (candidate.hasConflict) return false;
      return isValidCandidate(candidate.candidateId);
    }).length;

  const getTotalScorable = () =>
    sortedCandidates.filter((candidate) => !candidate.hasConflict).length;

  const getCurrentElectionTypeName = () => {
    const currentType = availableElectionTypes.find(
      (type) => type.id === activeElectionTypeId,
    );
    return currentType?.name || '';
  };

  const hasValidationErrors =
    Object.keys(validationErrors).length > 0 &&
    Object.values(scoringData).some((candidateScores) =>
      matrix.criteria.some(
        (criterion) => (candidateScores[criterion.key] || 0) > 0,
      ),
    );

  return (
    <div className="scoring-view mb-xl-5">
      <PageTitle
        motherMenu="Nominations"
        activeMenu="Candidate Scoring"
        notificationMessage={notificationMessage}
        notificationTitle={notificationTitle}
      />

      <div className="d-flex justify-content-between align-items-center mb-4">
        <Button
          variant="outline-primary"
          onClick={onBackToShortlist}
          className="d-flex align-items-center gap-2"
        >
          <i className="fas fa-arrow-left" />
          Back to Candidates
        </Button>

        <div className="text-end" style={{ maxWidth: '25rem' }}>
          <h4 className="mb-1 fw-bold lh-sm text-break">
            {committeeInfo.name}
          </h4>
          <small className="text-muted">{getCurrentElectionTypeName()}</small>
        </div>
      </div>

      <ElectionTypeToggle
        availableTypes={availableElectionTypes}
        activeType={activeElectionTypeId}
        onTypeChange={setActiveElectionTypeId}
        canScoreMultipleTypes={canScoreMultipleTypes}
      />

      <Card className="mb-4">
        <Card.Header>
          <Row className="align-items-center">
            <Col md={8}>
              <div className="d-flex align-items-center">
                <i className="fas fa-info-circle me-2" />
                <span>{matrix.introduction}</span>
              </div>
            </Col>
            <Col md={4} className="text-end">
              <div className="d-flex justify-content-end align-items-center gap-4">
                <div className="text-center">
                  <div className="fs-4 fw-bold">
                    {getScoredCount()}/{getTotalScorable()}
                  </div>
                  <small>Completed</small>
                </div>
                <div className="text-center">
                  <div className="fs-4 fw-bold">{getTotalScorable()}</div>
                  <small>Total</small>
                </div>
              </div>
            </Col>
          </Row>
        </Card.Header>
      </Card>

      {hasValidationErrors && (
        <Alert variant="danger" className="mb-4">
          <p>
            <i className="fas fa-info-circle me-2" /> The following candidates
            have incomplete or invalid scores:
          </p>
          <div className="mb-0">
            {sortedCandidates
              .filter((candidate) => validationErrors[candidate.candidateId])
              .map((candidate) => {
                const errors = validationErrors[candidate.candidateId];
                return (
                  <div key={candidate.candidateId} className="mb-3">
                    <div className="fw-bold">{candidate.name}:</div>
                    <ul className="mt-1 mb-0">
                      {errors.map((error, index) => (
                        <li key={index} className="small">
                          {error}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
          </div>
        </Alert>
      )}

      <Card>
        <Card.Body className="p-0">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-primary">
                <tr>
                  <th className="py-3 scoring-view__candidate-column">
                    <span className="fw-bold">Candidate</span>
                  </th>
                  {matrix.criteria.map((criterion) => (
                    <th
                      key={criterion.key}
                      className="text-center py-3 scoring-view__header-column"
                    >
                      <div className="d-flex flex-column">
                        <div className="fw-bold mb-1 d-flex align-items-center justify-content-center scoring-view__header-title">
                          {criterion.label}
                        </div>
                        <div className="small text-muted mb-3 d-flex align-items-start justify-content-center scoring-view__header-desc">
                          {criterion.description}
                        </div>
                        <div>
                          <GuidelinesButton
                            criterionKey={criterion.key}
                            onShowGuidelines={handleShowGuidelines}
                          />
                        </div>
                      </div>
                    </th>
                  ))}
                  <th className="text-center py-3 scoring-view__total-column">
                    <span className="fw-bold">Total Score</span>
                  </th>
                  <th className="py-3 scoring-view__comments-column">
                    <span className="fw-bold">Comments</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedCandidates.map((candidate) => (
                  <CandidateRow
                    key={candidate.candidateId}
                    candidate={candidate}
                    scores={scoringData[candidate.candidateId] || {}}
                    matrix={matrix}
                    onScoreChange={handleScoreChange}
                    onCommentsChange={handleCommentsChange}
                    hasConflict={candidate.hasConflict}
                    validationErrors={
                      validationErrors[candidate.candidateId] || []
                    }
                    isLoading={isLoading}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Card.Body>
      </Card>

      <div className="d-flex justify-content-end mt-4">
        <Button
          variant="success"
          onClick={handleSaveAndReturn}
          disabled={isLoading}
          className="px-5 standard-width-button"
        >
          {isLoading ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Loading
            </>
          ) : (
            'Save'
          )}
        </Button>
      </div>

      <GuidelinesModal
        show={showGuidelines}
        onHide={() => setShowGuidelines(false)}
        criterionKey={currentGuideline}
        matrixType={matrix.type}
      />
    </div>
  );
};

ScoringView.propTypes = {
  onBackToShortlist: PropTypes.func.isRequired,
  notificationMessage: PropTypes.string,
  notificationTitle: PropTypes.string,
  useScoringResults: PropTypes.array,
};

ElectionTypeToggle.propTypes = {
  availableTypes: PropTypes.array.isRequired,
  activeType: PropTypes.number.isRequired,
  onTypeChange: PropTypes.func.isRequired,
  canScoreMultipleTypes: PropTypes.bool.isRequired,
};

GuidelinesButton.propTypes = {
  criterionKey: PropTypes.string.isRequired,
  onShowGuidelines: PropTypes.func.isRequired,
};

ScoreInput.propTypes = {
  value: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  adjustedMax: PropTypes.number,
  disabled: PropTypes.bool,
  isValid: PropTypes.bool,
};

CandidateRow.propTypes = {
  candidate: PropTypes.object.isRequired,
  scores: PropTypes.object,
  matrix: PropTypes.object.isRequired,
  onScoreChange: PropTypes.func.isRequired,
  onCommentsChange: PropTypes.func.isRequired,
  hasConflict: PropTypes.bool,
  validationErrors: PropTypes.array,
  isLoading: PropTypes.bool,
};

export default ScoringView;
